import React, {useState} from 'react';
import {
  Clipboard,
  ScrollView,
  StyleSheet,
  TouchableOpacity,
  View,
} from 'react-native';

import CCColors from '@/Utils/CCColors';
import {FontSizes, Spacing} from '@/Utils/Dimensions';
import CCText from '@/Atoms/CCText';
import CCButton from '@/Atoms/CCButton';
import Spacer from '@/Atoms/Spacer';

interface Props {
  solvedCount: number;
  totalPuzzles: number;
  couponCode?: string;
  couponDiscount?: number;
  couponExpiresAt?: string;
  isGeneratingCoupon: boolean;
  onRetry: () => void;
  onRenewNow: () => void;
  onBack: () => void;
}

export default function PuzzleChallengeResult({
  solvedCount,
  totalPuzzles,
  couponCode,
  couponDiscount,
  couponExpiresAt,
  isGeneratingCoupon,
  onRetry,
  onRenewNow,
  onBack,
}: Props) {
  const [copied, setCopied] = useState(false);
  const isHighScore = solvedCount >= 5;

  const handleCopy = () => {
    if (!couponCode) return;
    Clipboard.setString(couponCode);
    setCopied(true);
    setTimeout(() => setCopied(false), 2000);
  };

  const expiryDisplay = couponExpiresAt
    ? new Date(couponExpiresAt).toLocaleDateString('en-IN', {
        day: 'numeric',
        month: 'short',
        year: 'numeric',
      })
    : '';

  if (totalPuzzles === 0) {
    return (
      <ScrollView contentContainerStyle={styles.screen}>
        <View style={styles.heroBgFail}>
          <View style={styles.card}>
            <CCText fontSize={FontSizes[40]} style={styles.centered}>✨</CCText>
            <Spacer spacing={8} />
            <CCText fontSize={FontSizes[20]} fontWeight="bold" color={CCColors.Primary.Brown} style={styles.centered}>
              You're all clear!
            </CCText>
            <Spacer spacing={6} />
            <CCText fontSize={FontSizes[14]} color={CCColors.TextColor.Grey} style={[styles.centered, {lineHeight: 20}]}>
              You don't have enough mistakes from your recent games to generate a puzzle challenge right now. Play some live games on Chess.com or Lichess to generate puzzles for next time!
            </CCText>
            <Spacer spacing={24} />
            <CCButton type="fill" onPress={onRenewNow} stretched>
              Renew Now
            </CCButton>
            <Spacer spacing={10} />
            <CCButton type="outline" onPress={onBack} stretched>
              Back to Dashboard
            </CCButton>
          </View>
        </View>
      </ScrollView>
    );
  }

  if (!isHighScore) {
    return (
      <ScrollView contentContainerStyle={styles.screen}>
        <View style={styles.heroBgFail}>
          <View style={styles.card}>
            <CCText fontSize={FontSizes[40]} style={styles.centered}>😔</CCText>
            <Spacer spacing={8} />
            <View style={styles.scoreRow}>
              <CCText fontSize={FontSizes[48]} fontWeight="bold" color={CCColors.Primary.Brown}>
                {solvedCount}
              </CCText>
              <CCText fontSize={FontSizes[28]} color={CCColors.TextColor.Grey} style={styles.scoreSlash}>
                /{totalPuzzles}
              </CCText>
            </View>
            <CCText fontSize={FontSizes[14]} color={CCColors.TextColor.Grey} style={styles.centered}>
              puzzles solved
            </CCText>
            <Spacer spacing={16} />

            {/* Info section */}
            <View style={styles.divider} />
            <Spacer spacing={16} />
            <View style={styles.infoIconRow}>
              <CCText fontSize={FontSizes[18]}>🎯</CCText>
              <CCText fontSize={FontSizes[15]} fontWeight="bold" color={CCColors.Primary.Brown}>
                So close!
              </CCText>
            </View>
            <Spacer spacing={6} />
            <CCText fontSize={FontSizes[13]} color={CCColors.TextColor.Grey} style={styles.centered}>
              Solve at least 5 puzzles to earn a discount coupon.
              Each correct answer = 1% off your renewal.
            </CCText>
            <Spacer spacing={14} />
            <View style={styles.progressBarBg}>
              <View style={[styles.progressBarFill, {width: `${(solvedCount / totalPuzzles) * 100}%` as any}]} />
              <View style={styles.progressThreshold} />
            </View>
            <View style={styles.progressLabels}>
              <CCText fontSize={FontSizes[11]} color={CCColors.TextColor.Grey}>0</CCText>
              <CCText fontSize={FontSizes[11]} color={CCColors.Primary.Brown} fontWeight="bold">5 ← goal</CCText>
              <CCText fontSize={FontSizes[11]} color={CCColors.TextColor.Grey}>{totalPuzzles}</CCText>
            </View>
            <Spacer spacing={24} />
            <CCButton type="fill" onPress={onRetry} stretched>
              Try Again
            </CCButton>
            <Spacer spacing={10} />
            <CCButton type="outline" onPress={onBack} stretched>
              Back to Dashboard
            </CCButton>
          </View>
        </View>
      </ScrollView>
    );
  }

  return (
    <ScrollView contentContainerStyle={styles.screen}>
      <View style={styles.heroBgWin}>
        <View style={styles.card}>
          {/* Trophy + score */}
          <CCText fontSize={FontSizes[44]} style={styles.centered}>🏆</CCText>
          <Spacer spacing={8} />
          <View style={styles.scoreRow}>
            <CCText fontSize={FontSizes[36]} fontWeight="bold" color={CCColors.Primary.Brown}>
              {solvedCount}
            </CCText>
            <CCText fontSize={FontSizes[36]} color={CCColors.TextColor.Grey} style={styles.scoreSlash}>
              /{totalPuzzles}
            </CCText>
          </View>
          <CCText fontSize={FontSizes[13]} color={CCColors.TextColor.Grey} style={styles.centered}>
            puzzles solved
          </CCText>
          <Spacer spacing={10} />
          <View style={styles.discountPill}>
            <CCText fontSize={FontSizes[15]} fontWeight="bold" color={CCColors.Primary.Brown}>
              🎉 You earned {couponDiscount ?? solvedCount}% off!
            </CCText>
          </View>

          <Spacer spacing={20} />
          <View style={styles.divider} />
          <Spacer spacing={20} />

          {/* Coupon card */}
          {isGeneratingCoupon ? (
            <View style={styles.couponCard}>
              <CCText fontSize={FontSizes[13]} color={CCColors.TextColor.Grey} style={styles.centered}>
                ⏳ Generating your coupon…
              </CCText>
            </View>
          ) : couponCode ? (
            <View style={styles.couponCard}>
              <CCText
                fontSize={FontSizes[11]}
                color={CCColors.Primary.Brown}
                style={[styles.centered, styles.couponLabel]}>
                YOUR COUPON CODE
              </CCText>
              <Spacer spacing={8} />
              <CCText
                fontSize={FontSizes[22]}
                fontWeight="bold"
                color={CCColors.Primary.Brown}
                style={[styles.centered, styles.couponCodeText]}>
                {couponCode}
              </CCText>
              <Spacer spacing={6} />
              <CCText fontSize={FontSizes[12]} color={CCColors.TextColor.Grey} style={styles.centered}>
                {couponDiscount}% off · Valid until {expiryDisplay}
              </CCText>
              <Spacer spacing={14} />
              <TouchableOpacity style={styles.copyBtn} onPress={handleCopy}>
                <CCText fontSize={FontSizes[13]} fontWeight="bold" color={copied ? CCColors.Green : CCColors.Primary.Brown}>
                  {copied ? '✓ Copied!' : '📋 Copy Code'}
                </CCText>
              </TouchableOpacity>
            </View>
          ) : null}

          {/* Hint */}
          {couponCode && (
            <>
              <Spacer spacing={12} />
              <View style={styles.hintCard}>
                <CCText fontSize={FontSizes[12]} color={CCColors.TextColor.Grey} style={styles.centered}>
                  Tap{' '}
                  <CCText fontSize={FontSizes[12]} fontWeight="bold" color={CCColors.Primary.Brown}>
                    Renew Now
                  </CCText>
                  {' '}below — your coupon will be pre-applied automatically.
                </CCText>
              </View>
            </>
          )}

          <Spacer spacing={24} />
          <CCButton
            type="fill"
            onPress={onRenewNow}
            disabled={isGeneratingCoupon}
            stretched>
            {`Renew Now${couponDiscount ? ` — Use ${couponDiscount}% Off` : ''}`}
          </CCButton>
          <Spacer spacing={10} />
          <CCButton type="outline" onPress={onBack} stretched>
            Back to Dashboard
          </CCButton>
        </View>
      </View>
    </ScrollView>
  );
}

const styles = StyleSheet.create({
  screen: {
    flexGrow: 1,
    backgroundColor: CCColors.Primary.Light,
    alignItems: 'center',
  },
  heroBgWin: {
    width: '100%',
    alignItems: 'center',
    paddingVertical: 32,
    paddingHorizontal: 16,
    backgroundColor: CCColors.Primary.Light,
  },
  heroBgFail: {
    width: '100%',
    alignItems: 'center',
    paddingVertical: 32,
    paddingHorizontal: 16,
    backgroundColor: CCColors.Primary.Light,
  },
  card: {
    width: '100%',
    maxWidth: 480,
    backgroundColor: CCColors.Background,
    borderRadius: 16,
    padding: Spacing.large,
    alignItems: 'center',
    borderWidth: 1.5,
    borderColor: CCColors.CCTableBg,
    // shadow
    shadowColor: CCColors.Primary.Brown,
    shadowOffset: {width: 0, height: 4},
    shadowOpacity: 0.08,
    shadowRadius: 12,
    elevation: 4,
  },
  divider: {
    width: '100%',
    height: 1,
    backgroundColor: CCColors.CCTableBg,
  },
  scoreRow: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: 2,
  },
  scoreSlash: {},
  discountPill: {
    backgroundColor: CCColors.Primary.Default,
    borderRadius: 20,
    paddingHorizontal: 18,
    paddingVertical: 7,
  },
  centered: {
    textAlign: 'center',
  },
  infoIconRow: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: 8,
  },
  progressBarBg: {
    width: '100%',
    height: 8,
    backgroundColor: CCColors.CCTableBg,
    borderRadius: 4,
    overflow: 'hidden',
    position: 'relative',
  },
  progressBarFill: {
    height: '100%',
    backgroundColor: CCColors.Primary.Default,
    borderRadius: 4,
  },
  progressThreshold: {
    position: 'absolute',
    left: '50%',
    top: -3,
    width: 2,
    height: 14,
    backgroundColor: CCColors.Primary.Brown,
    borderRadius: 1,
  },
  progressLabels: {
    flexDirection: 'row',
    justifyContent: 'space-between',
    width: '100%',
    marginTop: 4,
  },
  couponCard: {
    width: '100%',
    backgroundColor: CCColors.Primary.Light,
    borderRadius: 12,
    padding: Spacing.regular,
    borderWidth: 2,
    borderStyle: 'dashed',
    borderColor: CCColors.Primary.Brown,
    alignItems: 'center',
  },
  couponLabel: {
    letterSpacing: 1.5,
  },
  couponCodeText: {
    letterSpacing: 3,
  },
  copyBtn: {
    borderRadius: 8,
    paddingHorizontal: 24,
    paddingVertical: 8,
    borderWidth: 1.5,
    borderColor: CCColors.Primary.Brown,
    backgroundColor: CCColors.Background,
  },
  hintCard: {
    width: '100%',
    backgroundColor: CCColors.CCTableBg,
    borderRadius: 8,
    paddingHorizontal: 14,
    paddingVertical: 10,
    alignItems: 'center',
  },
});
